// =============================================================================
// Baby Hemp — Central product catalog (SINGLE SOURCE OF TRUTH)
// -----------------------------------------------------------------------------
// SHOPIFY-READY: All product data lives here in one place. When Shopify is
// connected, replace the values below with data fetched from the Shopify
// Storefront API, keeping the same object shape (id, name, price, image,
// variants, compareAt). UI components only read from this module, so prices,
// names, images, bundles and discounts can be swapped without touching the UI.
// Prices are placeholders in GBP.
// =============================================================================

export const CURRENCY = "£";

export const PRODUCTS = {
  patch: {
    id: "baby-hemp-eczema-patches",
    shopifyId: null,
    handle: "patch",
    name: "Baby Hemp Eczema Patches",
    subtitle: "Pack of 10 hydrocolloid patches",
    price: 15.99,
    compareAt: null,
    image: "/assets/patch.png",
    badge: "Pack of 10",
    tagline: "Soothes · Protects · Supports",
    description:
      "Gentle hydrocolloid patches that help protect irritated, eczema-prone skin from scratching and rubbing — soothing relief for day and night.",
    benefits: [
      "Helps protect skin from scratching",
      "Supports the natural skin barrier",
      "Locks in moisture for dry, sensitive skin",
      "Dermatologically gentle — suitable for ages 6 months+",
    ],
    ingredients:
      "Hydrocolloid matrix, medical-grade adhesive, hemp seed oil extract. Free from fragrance, parabens and dyes.",
    howToUse: [
      "Clean and dry the affected area gently.",
      "Peel a patch and apply it smoothly over the irritated skin.",
      "Leave on overnight or throughout the day.",
      "Replace with a fresh patch as needed.",
    ],
    warnings:
      "For external use only. Do not apply to broken or infected skin. Discontinue use if irritation occurs. Keep away from the mouth and eyes. Always supervise young children.",
    contents: "10 individually sealed hydrocolloid patches.",
    gallery: [
      { type: "3d", src: "/assets/pouch.glb" },
      { type: "img", src: "/assets/patch-arm.png" },
      { type: "img", src: "/assets/patch.png" },
    ],
  },
  balm: {
    id: "baby-hemp-calm-balm-stick",
    shopifyId: null,
    handle: "balm",
    name: "Baby Hemp Calm Balm Stick",
    subtitle: "50ml mess-free balm stick",
    price: 14.99,
    compareAt: null,
    image: "/assets/balm.png",
    badge: "50ml",
    tagline: "Mess-free · Fast relief",
    description:
      "A nourishing, mess-free balm stick to moisturise and support dry, sensitive skin. Glides on smoothly with no sticky fingers.",
    benefits: [
      "Deeply moisturises dry, sensitive skin",
      "Supports a healthy skin barrier",
      "Mess-free, glide-on application",
      "Fragrance free — suitable from newborn",
    ],
    ingredients:
      "Hemp seed oil, shea butter, colloidal oatmeal, beeswax, vitamin E. Free from fragrance and synthetic dyes.",
    howToUse: [
      "Glide the balm directly onto clean, dry skin.",
      "Apply a thin, even layer over dry or irritated areas.",
      "Use morning and night, or as needed.",
    ],
    warnings:
      "For external use only. Avoid contact with eyes. Patch test before first use. Discontinue if irritation occurs.",
    contents: "1 x 50ml Calm Balm Stick.",
    gallery: [
      { type: "3d", src: "/assets/balm.glb" },
      { type: "img", src: "/assets/balm.png" },
    ],
  },
  bundle: {
    id: "baby-hemp-patch-balm-bundle",
    shopifyId: null,
    handle: "bundle",
    name: "Patch + Balm Bundle",
    subtitle: "10 patches + 1 Calm Balm Stick",
    price: 27.99,
    compareAt: 30.98,
    image: "/assets/bundle.png",
    badge: "Best for starting",
    tagline: "The complete two-step routine",
    description:
      "Everything you need for the simple two-step routine: protect with the patch, then moisturise with the balm. Save when you buy them together.",
    benefits: [
      "Complete protect + moisturise routine",
      "Helps stop scratching and supports the barrier",
      "Better value than buying separately",
      "Gentle, fragrance-free formulas",
    ],
    ingredients:
      "Includes the Eczema Patches (hydrocolloid, hemp seed oil extract) and the Calm Balm Stick (hemp seed oil, shea butter, colloidal oatmeal, beeswax, vitamin E).",
    howToUse: [
      "Step 1 — Apply a patch to help protect irritated areas from scratching.",
      "Step 2 — Glide the balm over dry, sensitive skin to moisturise.",
      "Repeat daily as part of a gentle skincare routine.",
    ],
    warnings:
      "For external use only. Do not apply to broken or infected skin. Avoid eyes and mouth. Discontinue use if irritation occurs.",
    contents: "1 pack of 10 patches + 1 x 50ml Calm Balm Stick.",
    gallery: [{ type: "img", src: "/assets/bundle.png" }],
  },
};

export const PRODUCT_LIST = [PRODUCTS.balm, PRODUCTS.patch, PRODUCTS.bundle];

// Subscribe & Save — Shopify selling plans will manage these later.
export const SUBSCRIBE_DISCOUNT = 0.15; // 15% off
export const SUBSCRIBE_INTERVALS = [
  { id: "2w", label: "Every 2 weeks" },
  { id: "4w", label: "Every 4 weeks", popular: true },
  { id: "6w", label: "Every 6 weeks" },
  { id: "8w", label: "Every 8 weeks" },
];

// Bundle Builder options
export const BUNDLE_OPTIONS = [
  {
    id: "balm-only",
    name: "Balm Only",
    detail: "1 Calm Balm Stick",
    price: 14.99,
    compareAt: null,
    image: "/assets/balm.png",
  },
  {
    id: "patch-only",
    name: "Patch Pack Only",
    detail: "1 pack · 10 patches",
    price: 15.99,
    compareAt: null,
    image: "/assets/patch.png",
  },
  {
    id: "patch-balm",
    name: "Patch + Balm Bundle",
    detail: "10 patches + 1 Balm Stick",
    price: 27.99,
    compareAt: 30.98,
    image: "/assets/bundle.png",
    popular: true,
  },
  {
    id: "3-pack",
    name: "3 Pack Patch Bundle",
    detail: "3 packs · 30 patches",
    price: 42.99,
    compareAt: 47.97,
    image: "/assets/patch.png",
  },
  {
    id: "6-pack",
    name: "6 Pack Patch Bundle",
    detail: "6 packs · 60 patches",
    price: 79.99,
    compareAt: 95.94,
    image: "/assets/patch.png",
  },
  {
    id: "family",
    name: "Family Care Bundle",
    detail: "3 packs of patches + 1 balm",
    price: 54.99,
    compareAt: 62.96,
    image: "/assets/bundle.png",
  },
];

export const WHY_LOVE = [
  {
    icon: "ShieldCheck",
    title: "Helps protect from scratching",
    text: "A gentle barrier that shields irritated areas from rubbing and scratching.",
  },
  {
    icon: "Sparkles",
    title: "Supports the skin barrier",
    text: "Formulated to help maintain and support your little one's natural skin barrier.",
  },
  {
    icon: "Feather",
    title: "Gentle for sensitive skin",
    text: "Fragrance-free and dermatologically gentle, made with sensitive skin in mind.",
  },
  {
    icon: "Heart",
    title: "Simple daily routine",
    text: "An easy two-step routine that fits calmly into your everyday care.",
  },
];

export const INGREDIENTS = [
  {
    icon: "Leaf",
    name: "Hemp Seed Oil",
    text: "Rich in nourishing fatty acids to soften and soothe dry, sensitive skin.",
  },
  {
    icon: "Droplet",
    name: "Shea Butter",
    text: "Deeply moisturising butter that helps lock in hydration.",
  },
  {
    icon: "Wheat",
    name: "Colloidal Oatmeal",
    text: "A skin-soothing classic, gentle enough for the most delicate skin.",
  },
  {
    icon: "Sun",
    name: "Vitamin E",
    text: "An antioxidant that helps protect and support the skin barrier.",
  },
];

export const REVIEWS = [
  {
    name: "Emma R.",
    role: "Mum of two",
    rating: 5,
    text: "The patches have been a lifesaver overnight. My daughter finally stopped scratching and could sleep through.",
  },
  {
    name: "James P.",
    role: "Dad of one",
    rating: 5,
    text: "Love how simple the routine is. The balm glides on with zero mess and our little one doesn't fuss.",
  },
  {
    name: "Sofia M.",
    role: "Mum of three",
    rating: 5,
    text: "Gentle, fragrance free and it actually works for our sensitive skin. The bundle is great value too.",
  },
  {
    name: "Aisha K.",
    role: "First-time mum",
    rating: 4,
    text: "Really calming for flare-ups. Lovely soft packaging and it feels like a premium, trustworthy brand.",
  },
  {
    name: "Daniel T.",
    role: "Dad of two",
    rating: 5,
    text: "Subscribe & save means we never run out. Pausing was easy when we travelled. Highly recommend.",
  },
  {
    name: "Grace L.",
    role: "Mum of one",
    rating: 5,
    text: "Finally something gentle that doesn't irritate. The two-step routine has become part of bath time.",
  },
];

export const FAQS = [
  {
    q: "What age is Baby Hemp suitable for?",
    a: "The Calm Balm Stick is suitable from newborn. The Eczema Patches are suitable for ages 6 months and up. Always supervise young children during use.",
  },
  {
    q: "Are the products fragrance free?",
    a: "Yes. All Baby Hemp products are fragrance free and made with sensitive, eczema-prone skin in mind.",
  },
  {
    q: "How do the patches work?",
    a: "The hydrocolloid patches create a soft, breathable barrier that helps protect irritated skin from scratching and rubbing while helping lock in moisture.",
  },
  {
    q: "How does Subscribe & Save work?",
    a: "Choose a delivery frequency that suits you. You can pause, skip or cancel anytime — no commitment, no hassle.",
  },
  {
    q: "Is Baby Hemp a medical product?",
    a: "Baby Hemp is a gentle skincare support range, not a medical treatment. If symptoms persist or worsen, please speak to your healthcare professional.",
  },
  {
    q: "What is your returns policy?",
    a: "If you're not happy, contact us within 30 days and we'll make it right. Unopened products can be returned for a refund.",
  },
];
